/* eslint-disable sonarjs/no-clear-text-protocols */
import { HttpErrorResponse, HttpEvent, HttpRequest } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';

import { RwcInterceptor } from '../http/rwc-interceptor';
import { setHeader } from '../http/set-header';

import {
    ProblemJson,
    ProblemJsonCall,
    ProblemJsonUserMessage,
    ProblemJsonV2,
    ProblemJsonV2Call,
    ValidationProblemJsonV2
} from './problem-json.types';

/**
 * @deprecated: will be removed, use ProblemJsonV2 types
 */
type ProblemJsonKindV1 = '1';
type ProblemJsonKindV2 = 'jap-problem=2';
type ProblemJsonKind = ProblemJsonKindV1 | ProblemJsonKindV2;

const PROBLEM_JSON_VERSION_V1: ProblemJsonKind = '1';
const PROBLEM_JSON_VERSION_V2: ProblemJsonKind = 'jap-problem=2';

export interface ProvideProblemType {
    provide: InjectionToken<ProblemJsonKind>;
    useValue: ProblemJsonKind;
}

/**
 * @deprecated: will be removed, use ProblemJsonV2 types
 */
export const provideProblemJsonV1: () => ProvideProblemType = () => ({
    provide: PROBLEM_JSON_VERSION_INJECTION_TOKEN,
    useValue: PROBLEM_JSON_VERSION_V1
});
export const provideProblemJsonV2: () => ProvideProblemType = () => ({
    provide: PROBLEM_JSON_VERSION_INJECTION_TOKEN,
    useValue: PROBLEM_JSON_VERSION_V2
});

export const PROBLEM_JSON_VERSION_INJECTION_TOKEN = new InjectionToken<ProblemJsonKind>('PROBLEM_JSON_VERSION_TOKEN', {
    providedIn: 'root',
    factory: (): ProblemJsonKind => PROBLEM_JSON_VERSION_V2
});

/**
 * This interceptor prepends the 'accept' header field with 'application/problem+json;jap-problem=2' in case a json response is requested.
 * In addition, it maps error responses of type Jes2Exception to ProblemJson objects. Test.
 */
@Injectable({
    providedIn: 'root'
})
export class ProblemJsonInterceptor implements RwcInterceptor {
    private static readonly ACCEPT_PROBLEM_JSON = 'application/problem+json';
    private static readonly ACCEPT_PROBLEM_JSON_V2 = 'application/problem+json;jap-problem=2';
    private static readonly ACCEPT_DEFAULT = 'application/json, text/plain, */*';

    readonly #problemJsonVersion = inject(PROBLEM_JSON_VERSION_INJECTION_TOKEN);

    interceptRequest(req: HttpRequest<unknown>): Observable<HttpRequest<unknown>> {
        return of(setHeader(req, 'accept', this.getAcceptWithProblemJson(req)));
    }

    /**
     * @description: Handles error responses from http requests and maps the error data as well as add possible missing information about the original call
     */
    interceptResponseError(request: HttpRequest<unknown>, response: HttpErrorResponse): Observable<HttpEvent<unknown>> {
        let error = response.error;
        if (this.isJes2Exception(error)) {
            error = this.convertJes2ExceptionToProblemJson(error, response, request);
        } else if (isProblemJson(error) && !error.call) {
            error.call = this.createProblemJsonV1Call(response, request);
        } else if (isProblemJsonV2(error) && !error.call) {
            error.call = this.createProblemJsonV2Call(response, request);
        }
        return throwError(() => ({
            ...response,
            error
        }));
    }

    private getAcceptWithProblemJson(request: HttpRequest<unknown>): string {
        const accept = (request.headers && request.headers.get('accept')) || ProblemJsonInterceptor.ACCEPT_DEFAULT;
        const acceptProblemJson =
            this.#problemJsonVersion === PROBLEM_JSON_VERSION_V1
                ? ProblemJsonInterceptor.ACCEPT_PROBLEM_JSON
                : ProblemJsonInterceptor.ACCEPT_PROBLEM_JSON_V2;
        return accept.startsWith('application/json') || accept.startsWith('*/*')
            ? `${acceptProblemJson}, ${accept}`
            : accept;
    }

    /**
     * @deprecated Will be removed
     */
    private isJes2Exception(exception: any): exception is Jes2HTTPException {
        return exception && exception.callContextUUID;
    }

    /**
     * @deprecated: will be removed, use ProblemJsonV2 types
     */
    private createProblemJsonV1Call(response: HttpErrorResponse, request: HttpRequest<unknown>): ProblemJsonCall {
        return {
            headers: this.getHeadersFromResponse(response),
            uri: request.url,
            status: response.status,
            method: request.method
        };
    }

    private createProblemJsonV2Call(response: HttpErrorResponse, request: HttpRequest<unknown>): ProblemJsonV2Call {
        return {
            headers: this.getHeadersFromResponse(response),
            uri: request.url,
            method: request.method,
            body: request.body ? JSON.stringify(request.body) : (request.body as undefined)
        };
    }

    private getHeadersFromResponse(response: HttpErrorResponse) {
        return response.headers.keys().reduce(
            (tempHeaders, key) => {
                tempHeaders[key] = response.headers.get(key);
                return tempHeaders;
            },
            {} as Record<string, any>
        );
    }

    /**
     *
     * @deprecated Will be removed
     */
    private convertJes2ExceptionToProblemJson(
        jes2Exception: Jes2HTTPException,
        response: HttpErrorResponse,
        request: HttpRequest<unknown>
    ): ProblemJson {
        const language = response.headers.get('x-language') || undefined;

        const userMessages = this.extractProblemJsonUserMessages(jes2Exception, language);

        const type = this.mapToProblemType(jes2Exception);

        return {
            type,
            userMessages,
            status: jes2Exception.httpStatusCode || 0,
            callContext: {
                correlationId: jes2Exception.callContextUUID,
                callId: undefined
            },
            userLanguage: language,
            call: this.createProblemJsonV1Call(response, request),
            cause: {
                detail: (jes2Exception.causes || []).shift()
            },
            stackTrace: jes2Exception.stacktrace,
            violations: (jes2Exception.constraintViolations || []).map(v => {
                return {
                    interpolatedMessage: v.interpolatedMessage,
                    messageTemplate: v.messageTemplate,
                    propertyPath: v.propertyPath,
                    invalidValue: v.invalidValue
                };
            })
        };
    }

    /**
     *
     * @deprecated Will be removed
     */
    private extractProblemJsonUserMessages(
        jes2Exception: Jes2HTTPException,
        language?: string
    ): ProblemJsonUserMessage[] {
        return (jes2Exception.localizedMessages || []).map(msg => {
            const localizedMessage = (msg.translation || [])
                .filter(
                    translation =>
                        language &&
                        translation.language &&
                        translation.language.toUpperCase() === language.toUpperCase()
                )
                .shift();
            return {
                messageKey: msg.message ? msg.message.code : undefined,
                arguments: msg.message ? msg.message.messageParameter : undefined,
                localizedMessage: localizedMessage ? localizedMessage.translation : undefined
            };
        });
    }

    /**
     *
     * @deprecated Will be removed
     */
    private mapToProblemType(jes2HttpException: Jes2HTTPException) {
        if (jes2HttpException.constraintViolations) {
            return 'http://ch.mobi.jap.problem/ValidationException/v1';
        }
        switch (jes2HttpException.httpStatusCode) {
            case 403:
                return 'http://ch.mobi.jap.problem/SecurityException/v1';
            case 404:
                return 'http://ch.mobi.jap.problem/NotFoundException/v1';
            default:
                if (
                    jes2HttpException.httpStatusCode &&
                    jes2HttpException.httpStatusCode >= 400 &&
                    jes2HttpException.httpStatusCode < 500
                ) {
                    return 'http://ch.mobi.jap.problem/BusinessException/v1';
                } else {
                    return 'http://ch.mobi.jap.problem/TechnicalException/v1';
                }
        }
    }
}

export function isAnyProblemJson(problem: any): problem is ProblemJson | ProblemJsonV2 {
    return isProblemJson(problem) || isProblemJsonV2(problem);
}

/**
 * @deprecated: will be removed, use ProblemJsonV2 types
 */
export function isProblemJson(problem: any): problem is ProblemJson {
    return (
        problem?.type?.match(
            /http:\/\/ch.mobi.jap.problem\/(Technical|Business|Security|NotFound|Validation)Exception/g
        ) ?? false
    );
}

const PROBLEM_V2_JSON_REGEXP = new RegExp(
    '^https:\\/\\/jap.mobi.ch\\/problem-types\\/jap' +
        '(?<japType>\\/validation)?' +
        '(' +
        '#(?<appId>[A-Za-z0-9]{3}-[A-Za-z0-9]{3,})' +
        '(-(?<fkName>[A-Za-z0-9]{3,}))?' +
        '(\\/(?<problemName>[A-Za-z0-9-]{3,}))?' +
        ')?' +
        '$',
    'g'
);

export function isProblemJsonV2(problem: any): problem is ProblemJsonV2 {
    return problem?.type?.match(PROBLEM_V2_JSON_REGEXP) ?? false;
}

export function isValidationProblemV2Json(problem: any): problem is ValidationProblemJsonV2 {
    return problem?.type?.match(/^https:\/\/jap.mobi.ch\/problem-types\/jap\/validation(#.+)?$/g) ?? false;
}

/**
 * @deprecated Will be removed
 */
interface Jes2HTTPException {
    localizedMessages?: Jes2HTTPLocalizedMessage[];
    messages?: Jes2HTTPMessage[];
    constraintViolations?: Jes2ConstraintViolation[];
    callContextUUID?: string;
    requestURL?: string;
    httpStatusCode?: number;
    httpStatusFamily?: string;
    httpStatusReasonPhrase?: string;
    causes?: string[];
    stacktrace?: string[];
}

/**
 * @deprecated Will be removed
 */
interface Jes2HTTPLocalizedMessage {
    message: Jes2HTTPMessage;
    translation: {
        language: string;
        translation: string;
    }[];
}

/**
 * @deprecated Will be removed
 */
interface Jes2HTTPMessage {
    code: string;
    severity: string;
    logMessage?: string;
    messageParameter?: string[];
}

/**
 * @deprecated Will be removed
 */
interface Jes2ConstraintViolation {
    propertyPath: string;
    interpolatedMessage: string;
    messageTemplate: string;
    invalidValue: string | boolean | number;
}
